import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ClipboardList, 
  CheckCircle2, 
  Send, 
  Package, 
  Truck,
  Clock,
  ArrowRight,
  AlertTriangle,
  TrendingUp,
  ThermometerSnowflake,
  PackageX,
  Scale,
  BarChart3,
  Euro,
  ShoppingCart,
  Wallet,
  Receipt,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  LineChart
} from 'recharts';

const DashboardPage = () => {
  const { api, user, isAdmin, isResponsable } = useAuth();
  const { isTrialExpired } = useTrialStatus();
  const navigate = useNavigate();
  
  // Mark this page as visited
  useMarkPageVisited('/dashboard');
  
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Analytics widgets state
  const [topProducts, setTopProducts] = useState([]);
  const [costTrends, setCostTrends] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, dashboardRes, ordersRes] = await Promise.all([
          api.get('/stats'),
          api.get('/stats/dashboard'),
          api.get('/orders?status=pending')
        ]);
        setStats(statsRes.data);
        setDashboardData(dashboardRes.data);
        setPendingOrders(ordersRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchAnalytics = async () => {
      try {
        const [productsRes, trendsRes] = await Promise.all([
          api.get('/stats/top-products'),
          api.get('/stats/cost-trends?days=30')
        ]);
        setTopProducts(productsRes.data);
        setCostTrends(trendsRes.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    
    fetchData();
    fetchAnalytics();
  }, [api]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick, subtitle }) => (
    <Card 
      className="border border-gray-200 hover:border-[#1B4332]/30 hover:shadow-md transition-all cursor-pointer"
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      validated: 'bg-blue-100 text-blue-800 border-blue-200',
      sent: 'bg-green-100 text-green-800 border-green-200',
      delivered: 'bg-[#D8F3DC] text-[#1B4332] border-[#1B4332]/20',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = {
      pending: 'En attente',
      validated: 'Validée',
      sent: 'Envoyée',
      delivered: 'Livrée',
      rejected: 'Rejetée'
    };
    return (
      <Badge variant="outline" className={styles[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name === 'amount' ? formatCurrency(entry.value) : `${entry.value} commandes`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" id="dashboard-root" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Tableau de bord
          </h1>
          <p className="text-gray-500 mt-1">
            {user?.team_name ? `Équipe ${user.team_name}` : 'Administration'} • {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
          </p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Vue d'ensemble de votre activité : commandes en cours, livraisons à réceptionner, statistiques et indicateurs clés.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => navigate('/catalog')}
            disabled={isTrialExpired}
            className="bg-[#1B4332] hover:bg-[#1B4332]/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="new-order-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Nouvelle commande
          </Button>
        </div>
      </div>

      {/* Financial Summary */}
      {dashboardData?.financial && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-gray-200 bg-gradient-to-br from-[#1B4332] to-[#2D5A45]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/70 font-medium">Chiffre total</p>
                  <p className="text-2xl font-bold mt-1 tabular-nums text-white">
                    {formatCurrency(dashboardData.financial.total_amount)}
                  </p>
                  <p className="text-xs text-white/50 mt-1">{dashboardData.summary.total_orders} commandes</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white/20">
                  <Euro className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">En cours</p>
                  <p className="text-2xl font-bold mt-1 tabular-nums text-amber-600">
                    {formatCurrency(
                      (dashboardData.financial?.pending_amount || 0) + 
                      (dashboardData.financial?.validated_amount || 0) + 
                      (dashboardData.financial?.sent_amount || 0)
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {(dashboardData.summary?.pending || 0) + 
                     (dashboardData.summary?.validated || 0) + 
                     (dashboardData.summary?.sent || 0)} commandes
                  </p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-100">
                  <ShoppingCart className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Livré</p>
                  <p className="text-2xl font-bold mt-1 tabular-nums text-green-600">
                    {formatCurrency(dashboardData.financial.delivered_amount)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{dashboardData.summary.delivered} commandes</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-green-100">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Panier moyen</p>
                  <p className="text-2xl font-bold mt-1 tabular-nums text-[#1B4332]">
                    {formatCurrency(dashboardData.financial.avg_order_value)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">par commande</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#D8F3DC]">
                  <Receipt className="w-5 h-5 text-[#1B4332]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard 
          title="En attente" 
          value={stats?.pending_orders || 0} 
          icon={Clock}
          color="bg-amber-100 text-amber-700"
          onClick={() => navigate('/orders?status=pending')}
        />
        <StatCard 
          title="Validées" 
          value={stats?.validated_orders || 0} 
          icon={CheckCircle2}
          color="bg-blue-100 text-blue-700"
          onClick={() => navigate('/orders?status=validated')}
        />
        <StatCard 
          title="Envoyées" 
          value={stats?.sent_orders || 0} 
          icon={Send}
          color="bg-green-100 text-green-700"
          onClick={() => navigate('/deliveries')}
        />
        <StatCard 
          title="Livrées" 
          value={stats?.delivered_orders || 0} 
          icon={Truck}
          color="bg-[#D8F3DC] text-[#1B4332]"
          onClick={() => navigate('/deliveries')}
        />
        {isAdmin && (
          <StatCard 
            title="Produits" 
            value={stats?.total_products || 0} 
            icon={Package}
            color="bg-gray-100 text-gray-700"
            onClick={() => navigate('/admin/products')}
          />
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders & Amount by Month */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#1B4332]" />
              Volume par mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {dashboardData?.orders_by_month && dashboardData.orders_by_month.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                  <ComposedChart data={dashboardData.orders_by_month} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11, fill: '#6B7280' }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      yAxisId="left"
                      dataKey="count" 
                      name="Commandes"
                      fill="#1B4332" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="amount"
                      name="amount"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Aucune donnée disponible
                </div>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-[#1B4332] rounded"></span> Commandes
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-amber-500 rounded-full"></span> Montant €
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#1B4332]" />
              Répartition par statut
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {dashboardData?.orders_by_status && dashboardData.summary?.total_orders > 0 ? (
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                  <PieChart>
                    <Pie
                      data={dashboardData.orders_by_status.filter(s => s.count > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="label"
                    >
                      {dashboardData.orders_by_status.filter(s => s.count > 0).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} commandes`, name]}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Aucune commande
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Supplier Volumes */}
      {dashboardData?.supplier_volumes && dashboardData.supplier_volumes.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Euro className="w-5 h-5 text-[#1B4332]" />
              Volume par fournisseur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Fournisseur</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Commandes</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Montant total</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Panier moyen</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Articles</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.supplier_volumes.map((supplier, index) => (
                    <tr key={supplier.supplier_id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-[#1B4332] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-xs">
                              {supplier.supplier_name.substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900">{supplier.supplier_name}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-semibold text-gray-900 tabular-nums">
                        {supplier.total_orders}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-[#1B4332] tabular-nums">
                        {formatCurrency(supplier.total_amount)}
                      </td>
                      <td className="text-right py-3 px-4 text-gray-600 tabular-nums">
                        {formatCurrency(supplier.avg_order_value)}
                      </td>
                      <td className="text-right py-3 px-4 text-gray-600 tabular-nums">
                        {supplier.total_items}
                      </td>
                      <td className="text-center py-3 px-4">
                        <div className="flex justify-center gap-2">
                          {supplier.pending_orders > 0 && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              {supplier.pending_orders} en attente
                            </Badge>
                          )}
                          {supplier.delivered_orders > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                              {supplier.delivered_orders} livrées
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Issues Summary */}
      {dashboardData?.recent_issues && dashboardData.recent_issues.total_deliveries > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Contrôle qualité (30 derniers jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D8F3DC] rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#1B4332]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardData.recent_issues.total_deliveries}</p>
                    <p className="text-xs text-gray-500">Livraisons</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1B4332] rounded-lg flex items-center justify-center">
                    <Euro className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(dashboardData.recent_issues.total_amount)}</p>
                    <p className="text-xs text-gray-500">Valeur livrée</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dashboardData.recent_issues.with_discrepancies > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${dashboardData.recent_issues.with_discrepancies > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardData.recent_issues.with_discrepancies}</p>
                    <p className="text-xs text-gray-500">Avec écarts</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dashboardData.recent_issues.temperature_issues > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <ThermometerSnowflake className={`w-5 h-5 ${dashboardData.recent_issues.temperature_issues > 0 ? 'text-red-600' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardData.recent_issues.temperature_issues}</p>
                    <p className="text-xs text-gray-500">Prob. temp.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dashboardData.recent_issues.quantity_issues > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                    <Scale className={`w-5 h-5 ${dashboardData.recent_issues.quantity_issues > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboardData.recent_issues.quantity_issues}</p>
                    <p className="text-xs text-gray-500">Écarts qté</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supplier Performance */}
      {dashboardData?.supplier_performance && dashboardData.supplier_performance.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#1B4332]" />
              Conformité fournisseurs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboardData.supplier_performance.map((supplier) => (
                <div key={supplier.supplier_id} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1B4332] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {supplier.supplier_name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{supplier.supplier_name}</p>
                        <p className="text-xs text-gray-500">{supplier.total_deliveries} livraisons contrôlées</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-lg font-bold ${supplier.conformity_rate >= 90 ? 'text-green-600' : supplier.conformity_rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {supplier.conformity_rate}%
                      </span>
                      <p className="text-xs text-gray-500">conformité</p>
                    </div>
                  </div>
                  <Progress 
                    value={supplier.conformity_rate} 
                    className="h-2"
                    indicatorClassName={supplier.conformity_rate >= 90 ? 'bg-green-500' : supplier.conformity_rate >= 70 ? 'bg-amber-500' : 'bg-red-500'}
                  />
                  {supplier.with_discrepancies > 0 && (
                    <div className="flex gap-4 mt-3 text-xs text-gray-500">
                      {supplier.quantity_issues > 0 && (
                        <span className="flex items-center gap-1">
                          <Scale className="w-3 h-3" /> {supplier.quantity_issues} écarts qté
                        </span>
                      )}
                      {supplier.temperature_issues > 0 && (
                        <span className="flex items-center gap-1">
                          <ThermometerSnowflake className="w-3 h-3" /> {supplier.temperature_issues} temp.
                        </span>
                      )}
                      {supplier.packaging_issues > 0 && (
                        <span className="flex items-center gap-1">
                          <PackageX className="w-3 h-3" /> {supplier.packaging_issues} cond.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics Widgets - Priority 2 Features */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Top 5 Products Widget */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5 text-[#1B4332]" />
              Top 5 Produits
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="dashboard-top-products-widget">
            {analyticsLoading ? (
              <div className="h-[260px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#1B4332]" />
              </div>
            ) : topProducts.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Pas assez de données</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {topProducts.slice(0, 5).map((product, index) => (
                  <div key={product.product_id} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-lg bg-[#D8F3DC] flex items-center justify-center">
                      <span className="text-[#1B4332] font-bold text-sm">#{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.article}</p>
                      <p className="text-xs text-gray-500">{product.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#1B4332] tabular-nums">{product.total_quantity}</p>
                      <p className="text-xs text-gray-500">{product.order_count} cmd</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost Trends Widget */}
        <Card className="border border-gray-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#1B4332]" />
                Tendances des Coûts (30 jours)
              </CardTitle>
              {costTrends && costTrends.trend_percentage !== 0 && (
                <Badge 
                  className={costTrends.trend_percentage > 0 
                    ? 'bg-red-100 text-red-700 border-red-200' 
                    : 'bg-green-100 text-green-700 border-green-200'
                  }
                  variant="outline"
                >
                  {costTrends.trend_percentage > 0 ? '↑' : '↓'} {Math.abs(costTrends.trend_percentage)}%
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent data-testid="dashboard-cost-trends-widget">
            {analyticsLoading ? (
              <div className="h-[260px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#1B4332]" />
              </div>
            ) : !costTrends || costTrends.daily_data.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Pas assez de données</p>
                </div>
              </div>
            ) : (
              <>
                <div className="h-[220px]" data-testid="cost-trends-chart">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <LineChart data={costTrends.daily_data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11, fill: '#6B7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#E5E7EB' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6B7280' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${value}€`}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                <p className="font-medium text-gray-900">{payload[0].payload.date}</p>
                                <p className="text-sm text-[#1B4332] font-semibold">
                                  {formatCurrency(payload[0].value)}
                                </p>
                                <p className="text-xs text-gray-500">{payload[0].payload.count} commandes</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#1B4332"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#1B4332' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total sur 30 jours</span>
                    <span className="font-bold text-[#1B4332] tabular-nums">
                      {formatCurrency(costTrends.total_cost)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending Orders */}
      {(isAdmin || isResponsable) && pendingOrders.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#1B4332]" />
              Commandes en attente de validation
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/orders?status=pending')}
              className="text-[#1B4332]"
            >
              Voir tout <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingOrders.map((order) => (
                <div 
                  key={order.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/orders/${order.id}`)}
                  data-testid={`pending-order-${order.order_number}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#1B4332] flex items-center justify-center">
                      <Truck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{order.order_number}</p>
                      <p className="text-sm text-gray-500">
                        {order.supplier_name} • {order.user_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="font-semibold tabular-nums">{formatCurrency(order.total_amount)}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(order.delivery_date), 'dd/MM/yyyy')}
                      </p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card 
          className="border border-gray-200 hover:border-[#1B4332]/30 hover:shadow-md transition-all cursor-pointer"
          onClick={() => navigate('/catalog')}
          data-testid="quick-action-catalog"
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
                <Package className="w-7 h-7 text-[#1B4332]" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Catalogue produits</h3>
                <p className="text-gray-500 text-sm">Parcourir et commander des produits</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border border-gray-200 hover:border-[#1B4332]/30 hover:shadow-md transition-all cursor-pointer"
          onClick={() => navigate('/deliveries')}
          data-testid="quick-action-deliveries"
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#F0EBE3] flex items-center justify-center">
                <Truck className="w-7 h-7 text-[#1B4332]" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Livraisons</h3>
                <p className="text-gray-500 text-sm">Réceptionner et contrôler les livraisons</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
